/**
 * Base Formatter Abstract Class
 * 
 * Provides the foundation for all field formatters with a consistent API,
 * error handling, and progressive formatting capabilities.
 */

import { FormattingResult, ValidationResult, FormattingOptions, ValidationRule } from '../types/formatting-types';

/**
 * Format operation context for internal processing
 */
export interface FormatContext {
  /** The raw input value */
  rawValue: string;
  /** Current cursor position (if available) */
  cursorPosition?: number;
  /** Whether this is progressive formatting (during typing) */
  isProgressive?: boolean;
  /** Previous formatted value for comparison */
  previousValue?: string;
  /** Additional context options */
  options?: FormattingOptions;
}

/**
 * Result of formatting operation including cursor management
 */
export interface ExtendedFormattingResult extends FormattingResult {
  /** New cursor position after formatting */
  newCursorPosition?: number;
  /** Whether the value was modified */
  wasModified: boolean;
  /** Partial formatting applied (for progressive formatting) */
  isPartial?: boolean;
}

/**
 * Abstract base class for all field formatters
 * 
 * Provides consistent interface and common functionality for
 * phone, ZIP, date, currency, and SSN formatters.
 */
export abstract class BaseFormatter {
  protected readonly formatType: string;
  protected readonly defaultOptions: FormattingOptions;

  constructor(formatType: string, defaultOptions: FormattingOptions = {}) {
    this.formatType = formatType;
    this.defaultOptions = defaultOptions;
  }

  /**
   * Format a value with progressive formatting support
   * 
   * @param value The raw input value
   * @param options Optional formatting configuration
   * @param cursorPosition Current cursor position for progressive formatting
   * @param isProgressive Whether this is real-time progressive formatting
   * @returns Extended formatting result with cursor position
   */
  public format(
    value: string, 
    options?: FormattingOptions,
    cursorPosition?: number,
    isProgressive: boolean = false
  ): ExtendedFormattingResult {
    try {
      // Sanitize input
      const sanitized = this.sanitizeInput(value);
      
      // Create format context
      const context: FormatContext = {
        rawValue: sanitized,
        cursorPosition,
        isProgressive,
        options: { ...this.defaultOptions, ...options }
      };

      // Perform formatting
      const result = this.performFormat(context);

      // Validate if needed
      let validationResult: ValidationResult | undefined;
      if (!isProgressive) {
        validationResult = this.validate(result.formattedValue, options);
      }

      return {
        formattedValue: result.formattedValue,
        rawValue: result.rawValue,
        success: result.success,
        errors: result.errors,
        newCursorPosition: result.newCursorPosition,
        wasModified: value !== result.formattedValue,
        isPartial: isProgressive && !this.isCompleteInput(result.rawValue)
      };

    } catch (error) {
      return {
        formattedValue: value,
        rawValue: value,
        success: false,
        errors: [`Formatting error: ${error}`],
        wasModified: false
      };
    }
  }

  /**
   * Extract raw value from formatted value
   * 
   * @param formattedValue The formatted value
   * @returns Raw unformatted value
   */
  public abstract extractRaw(formattedValue: string): string;

  /**
   * Validate a formatted value
   * 
   * @param value The value to validate
   * @param options Optional validation configuration
   * @returns Validation result
   */
  public validate(value: string, options?: FormattingOptions): ValidationResult {
    try {
      const rawValue = this.extractRaw(value);
      
      // Basic validation
      const errors: string[] = [];
      const warnings: string[] = [];

      // Check if value is empty
      if (!rawValue.trim()) {
        errors.push(`${this.formatType} cannot be empty`);
        return { isValid: false, errors, warnings };
      }

      // Perform specific validation
      const specificValidation = this.performValidation(rawValue, options);
      errors.push(...specificValidation.errors);
      warnings.push(...specificValidation.warnings || []);

      return {
        isValid: errors.length === 0,
        errors,
        warnings
      };

    } catch (error) {
      return {
        isValid: false,
        errors: [`Validation error: ${error}`],
        warnings: []
      };
    }
  }

  /**
   * Check if the formatter handles a specific field type
   * 
   * @param fieldType The field type to check
   * @returns Whether this formatter handles the field type
   */
  public abstract canHandle(fieldType: string): boolean;

  /**
   * Get the expected format pattern for display purposes
   * 
   * @returns Format pattern string (e.g., "XXX-XXX-XXXX")
   */
  public abstract getFormatPattern(): string;

  /**
   * Get formatter configuration information
   */
  public getFormatterInfo() {
    return {
      type: this.formatType,
      pattern: this.getFormatPattern(),
      defaultOptions: this.defaultOptions
    };
  }

  // Protected abstract methods for subclasses to implement

  /**
   * Perform the actual formatting logic
   * 
   * @param context Format context with all necessary information
   * @returns Extended formatting result
   */
  protected abstract performFormat(context: FormatContext): ExtendedFormattingResult;

  /**
   * Sanitize input by removing invalid characters
   * 
   * @param value Raw input value
   * @returns Sanitized value
   */
  protected abstract sanitizeInput(value: string): string;

  /**
   * Perform field-specific validation
   * 
   * @param rawValue The raw value to validate
   * @param options Optional validation configuration
   * @returns Validation result
   */
  protected abstract performValidation(rawValue: string, options?: FormattingOptions): ValidationResult;

  /**
   * Check if the input represents a complete value
   * 
   * @param rawValue The raw input value
   * @returns Whether the input is complete
   */
  protected abstract isCompleteInput(rawValue: string): boolean;

  // Protected utility methods

  /**
   * Calculate new cursor position after formatting
   * 
   * @param originalValue Original value before formatting
   * @param formattedValue Value after formatting
   * @param originalCursor Original cursor position
   * @returns New cursor position
   */
  protected calculateCursorPosition(
    originalValue: string,
    formattedValue: string,
    originalCursor?: number
  ): number | undefined {
    if (originalCursor === undefined) {
      return undefined;
    }

    // If lengths are the same, cursor position doesn't change
    if (originalValue.length === formattedValue.length) {
      return originalCursor;
    }

    // For now, place cursor at the end for safety
    // More sophisticated cursor positioning will be handled by cursor management system
    return formattedValue.length;
  }

  /**
   * Check if a character is valid for this formatter type
   * 
   * @param char Character to check
   * @returns Whether the character is valid
   */
  protected abstract isValidInputCharacter(char: string): boolean;

  /**
   * Remove formatting characters and return raw value
   * 
   * @param value Formatted value
   * @returns Raw value without formatting
   */
  protected abstract stripFormatting(value: string): string;

  /**
   * Log debug messages (can be overridden for specific logging needs)
   * 
   * @param message Message to log
   * @param level Log level
   */
  protected log(message: string, level: 'log' | 'warn' | 'error' = 'log'): void {
    // Only log in debug mode (can be configured per formatter)
    if (process.env.NODE_ENV === 'development') {
      console[level](`[${this.formatType}Formatter] ${message}`);
    }
  }
}

/**
 * Error class for formatter-specific errors
 */
export class FormatterError extends Error {
  constructor(
    public formatterType: string,
    message: string,
    public originalError?: Error
  ) {
    super(message);
    this.name = 'FormatterError';
  }
}